import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import { GMAIL_CLIENT_ID } from '../helpers/constants';

const AppsScriptAuthPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    let redirectUriFromUrl = searchParams.get('redirect_uri');
    
    if (redirectUriFromUrl) {
      // Decode the redirect URI if it's base64 encoded
      // (Apps Script: Utilities.base64EncodeWebSafe(uri))
      if (!redirectUriFromUrl.startsWith('http')) {
        try {
          let base64 = redirectUriFromUrl.replace(/-/g, '+').replace(/_/g, '/');
          while (base64.length % 4) base64 += '=';
          redirectUriFromUrl = atob(base64);
        } catch (e) {
          console.error('Failed to decode redirect_uri:', e);
        }
      }

      // Store the apps script redirect URI in session storage as a fallback
      sessionStorage.setItem('apps_script_redirect_uri', redirectUriFromUrl);
      
      // Perform OAuth flow similar to handleGmailOAuth
      const currentOrigin = window.location.origin;
      const redirectUri = `${currentOrigin}/auth/google/callback`;
      
      // Gmail API: readonly + send + modify (labels, trash, etc.); OIDC + profile for account identity
      const scope =
        'https://www.googleapis.com/auth/gmail.readonly https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile openid';
      const responseType = 'code';
      const accessType = 'offline';
      const prompt = 'select_account consent';
      
      // Encode the Apps Script redirect URI into the state parameter to ensure it survives the roundtrip
      const encodedRedirect = btoa(redirectUriFromUrl).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      const state = `apps_script_flow|${encodedRedirect}`;

      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${GMAIL_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `response_type=${responseType}&` +
        `access_type=${accessType}&` +
        `prompt=${encodeURIComponent(prompt)}&` +
        `state=${encodeURIComponent(state)}`; 
        
      // Redirect the current window to Google OAuth
      window.location.href = googleAuthUrl;
    }
  }, [searchParams]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress size={60} />
      <Typography variant="h6" color="text.secondary">
        Redirecting to Google...
      </Typography>
      {!searchParams.get('redirect_uri') && (
        <Typography color="error">
          Error: Missing redirect_uri parameter
        </Typography>
      )}
    </Box>
  );
};

export default AppsScriptAuthPage;
